import { useRef, useState } from 'react'
import { Arrow } from './ui'
import { clampRosterPage, portraitDisplayCrop, portraitFrameStyle, portraitWindowStyle, posterSource, posterWindowStyle, rosterPageLabels, rosterPages, rosterWaitingCount } from './contestant-roster'
import './contestant-roster.css'

export function RosterPage({ page }: { page: number }) {
  return <div className="roster-grid" id="roster-page" aria-label={`ผู้สมัครชุดที่ ${page + 1}`}>
    {rosterPages[page].map(slot => {
      const photoCrop = portraitDisplayCrop(slot)
      return slot.kind === 'contestant'
      ? <figure className={`roster-portrait${'photo' in slot ? ' roster-portrait--profile' : ''}`} key={slot.number} data-contestant-number={slot.number}>
        {'photo' in slot ? <>
          <div className="roster-photo">
            {slot.photo ? photoCrop
              ? <div className="roster-source-window" style={portraitFrameStyle(photoCrop)}>
                <img src={slot.photo} alt={`ผู้สมัครหมายเลข ${String(slot.number).padStart(2, '0')} ${slot.name}`}
                  width={photoCrop.sourceWidth} height={photoCrop.sourceHeight}
                  loading="lazy" decoding="async" style={portraitWindowStyle(photoCrop)} />
              </div>
              : <img src={slot.photo} alt={`ผู้สมัครหมายเลข ${String(slot.number).padStart(2, '0')} ${slot.name}`}
                loading="lazy" decoding="async" />
              : <div className="roster-waiting-image" role="img" aria-label={`รอรูปผู้สมัครหมายเลข ${slot.number} ${slot.name}`}>
                <span className="roster-question" aria-hidden="true">?</span><span>รอรูปผู้สมัคร</span>
              </div>}
            <span className="roster-number" aria-hidden="true">{String(slot.number).padStart(2, '0')}</span>
          </div>
          <figcaption className={`roster-profile-caption${slot.profession.length > 45 ? ' roster-profile-caption--long' : ''}`}><strong>{slot.name}</strong>{slot.profession && <p>{slot.profession}</p>}</figcaption>
        </> : <>
          <div className="roster-original-window" style={{ aspectRatio: `${slot.crop.width} / ${slot.crop.height}` }}>
            <img src={posterSource(slot.poster)} alt={`ผู้สมัครหมายเลข ${String(slot.number).padStart(2, '0')} ${slot.name} — รูปและรายละเอียดตามโปสเตอร์ต้นฉบับ`}
              width="2160" height="3240" loading="lazy" decoding="async" style={posterWindowStyle(slot.crop)} />
          </div>
          <figcaption className="sr-only">หมายเลข {slot.number} {slot.name}</figcaption>
        </>}
      </figure>
      : <div className="roster-waiting" key={slot.key} data-roster-placeholder="true" role="img" aria-label="กรอบรอใส่รูปผู้สมัคร">
        <div className="roster-waiting-image"><span className="roster-question" aria-hidden="true">?</span><span>PHOTO COMING SOON</span></div>
        <div className="roster-waiting-caption"><span>EMPRESS OF WELLNESS</span><small>รอใส่รูป</small></div>
      </div>
    })}
  </div>
}

export default function ContestantRoster() {
  const [page, setPage] = useState(0)
  const roster = useRef<HTMLDivElement>(null)
  const pointer = useRef<{ x: number; y: number } | null>(null)
  const selectPage = (next: number, fromBottom = false) => {
    setPage(clampRosterPage(next))
    if (fromBottom) roster.current?.scrollIntoView({ block: 'start', behavior: 'instant' })
  }

  return <div className="contestant-roster" ref={roster}>
    <div className="roster-heading"><span>EMPRESS <i>OF WELLNESS</i></span><h3>พบกับผู้สมัครของเรา</h3><p>ผู้หญิงวัย 35+ ที่พร้อมเติบโตในแบบตัวเอง</p></div>
    <nav className="roster-pages" aria-label="เลือกชุดภาพผู้สมัคร">
      {rosterPageLabels.map((label, index) => <button type="button" key={label} aria-current={page === index ? 'page' : undefined}
        aria-controls="roster-page" onClick={() => selectPage(index)}><span>{String(index + 1).padStart(2, '0')}</span>{label}</button>)}
    </nav>
    <div className="roster-page-surface" onTouchStart={event => { const touch = event.touches[0]; pointer.current = { x: touch.clientX, y: touch.clientY } }}
      onTouchEnd={event => {
        if (!pointer.current) return
        const touch = event.changedTouches[0]
        const dx = touch.clientX - pointer.current.x
        const dy = touch.clientY - pointer.current.y
        pointer.current = null
        if (Math.abs(dx) > 65 && Math.abs(dx) > Math.abs(dy) * 1.5) selectPage(page + (dx < 0 ? 1 : -1))
      }} onTouchCancel={() => { pointer.current = null }}>
      <RosterPage key={page} page={page} />
    </div>
    <div className="roster-navigation">
      <button type="button" onClick={() => selectPage(page - 1, true)} disabled={page === 0} aria-label="ดูผู้สมัครชุดก่อนหน้า"><span className="roster-arrow-back"><Arrow /></span><span>ก่อนหน้า</span></button>
      <p role="status" aria-live="polite" aria-atomic="true"><strong>{String(page + 1).padStart(2, '0')}</strong><span> / {String(rosterPages.length).padStart(2, '0')}</span><span className="sr-only"> — {rosterPageLabels[page]}</span></p>
      <button type="button" onClick={() => selectPage(page + 1, true)} disabled={page === rosterPages.length - 1} aria-label="ดูผู้สมัครชุดถัดไป"><span>ถัดไป</span><Arrow /></button>
    </div>
    {rosterWaitingCount > 0 && <p className="roster-waiting-note">ชุดสุดท้ายมี {rosterWaitingCount} กรอบรอใส่รูปเพิ่มเติม</p>}
  </div>
}
